import { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import type { NewsMode, ObjectivityRecord, Segment } from '../../types';
import { SEGMENT_COLORS } from '../../constants';
import SegmentToggle from '../common/SegmentToggle';
import Select, { type StylesConfig } from 'react-select';
import { tooltipStyles } from '../../utils/chartStyles';

interface ToneChartProps {
  data: Record<NewsMode, ObjectivityRecord[]>;
  locationOptions: string[];
  defaultLocation: string;
  onLocationChange: (location: string) => void;
  selectedLocation: string;
}

const selectStyles: StylesConfig<{ value: string; label: string }, false> = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: 'rgba(8,11,22,0.85)',
    borderColor: state.isFocused ? 'rgba(159,178,255,0.8)' : 'rgba(255,255,255,0.12)',
    boxShadow: 'none',
    minHeight: 44,
    cursor: 'pointer',
    color: '#f6f9ff'
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: '#070b18',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 12,
    overflow: 'hidden'
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? 'rgba(124,134,255,0.35)'
      : state.isFocused
        ? 'rgba(124,134,255,0.15)'
        : 'transparent',
    color: '#f6f9ff',
    cursor: 'pointer'
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#f6f9ff'
  }),
  input: (provided) => ({
    ...provided,
    color: '#f6f9ff'
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#9fa5bf'
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '2px 8px'
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: '#cfd4ee'
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    backgroundColor: 'rgba(255,255,255,0.1)'
  })
};

const ToneChart = ({
  data,
  locationOptions,
  defaultLocation,
  onLocationChange,
  selectedLocation
}: ToneChartProps) => {
  const [segment, setSegment] = useState<Segment>('Left');

  useEffect(() => {
    if (!locationOptions.includes(selectedLocation)) {
      onLocationChange(defaultLocation);
    }
  }, [locationOptions, selectedLocation, defaultLocation, onLocationChange]);

  const selectOptions = useMemo(
    () => locationOptions.map((loc) => ({ value: loc, label: loc })),
    [locationOptions]
  );

  const chartData = useMemo(() => {
    const records = data.political ?? [];
    const bySegment = records.filter((r) => r.segment === segment);
    const filtered = bySegment.filter(
      (r) => selectedLocation === 'All' || r.location === selectedLocation
    );

    if (filtered.length === 0) {
      return [];
    }

    const aggregated = filtered.reduce(
      (acc, curr) => ({
        opinion: acc.opinion + curr.opinion,
        fact: acc.fact + curr.fact,
        mixed: acc.mixed + curr.mixed,
        total: acc.total + curr.total,
        weightedObjectivity: acc.weightedObjectivity + curr.avgObjectivity * curr.total
      }),
      { opinion: 0, fact: 0, mixed: 0, total: 0, weightedObjectivity: 0 }
    );

    const total = aggregated.total || 1;
    const avgObjectivity = aggregated.weightedObjectivity / total;

    return [
      { label: 'Opinion', value: (aggregated.opinion / total) * 100, objectivity: avgObjectivity },
      { label: 'Mixed', value: (aggregated.mixed / total) * 100, objectivity: avgObjectivity },
      { label: 'Fact', value: (aggregated.fact / total) * 100, objectivity: avgObjectivity }
    ];
  }, [data, segment, selectedLocation]);

  return (
    <div>
      <div className="chart-controls stacked">
        <Select
          className="location-select"
          classNamePrefix="select"
          styles={selectStyles}
          options={selectOptions}
          value={{ value: selectedLocation, label: selectedLocation }}
          onChange={(option) => option && onLocationChange(option.value)}
          placeholder="Search locations"
        />
        <SegmentToggle value={segment} onChange={setSegment} />
      </div>
      <div className="chart-wrapper">
        {chartData.length === 0 && <p className="footnote">No tone data for this selection.</p>}
        {chartData.length > 0 && (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} layout="vertical" margin={{ top: 16, right: 32, left: 0, bottom: 0 }}>
              <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
              <YAxis type="category" dataKey="label" width={0} tick={false} axisLine={false} />
              <Tooltip
                cursor={{ fill: 'rgba(255,255,255,0.08)' }}
                formatter={(value: number, name, payload: any) => {
                  return [`article percentage: ${value.toFixed(1)}%`, name];
                }}
                labelFormatter={(label) => label}
                {...tooltipStyles}
              />
              <Bar dataKey="value" fill={SEGMENT_COLORS[segment]} radius={[0, 12, 12, 0]} barSize={30}>
                <LabelList
                  dataKey="label"
                  content={(props: any) => {
                    const { value, y = 0 } = props;
                    const labelX = props.viewBox?.x ?? 0;
                    return (
                      <text
                        x={labelX + 4}
                        y={y - 6}
                        fill="#ffffff"
                        fontSize={12}
                        textAnchor="start"
                      >
                        {value}
                      </text>
                    );
                  }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      <p className="footnote">Objectivity score ranges from 0 (opinion-heavy) to 1 (purely factual).</p>
    </div>
  );
};

export default ToneChart;
